import { getValidationErrorsForWorklistDialogInput, validateWorklistToDashboardDialogInputData, WorklistToDashboardDialogValidatedData } from '@/Pages/GenericWorklist/Logic/WorklistToDashboardValidationLogic';
import { ICurrentWorklistDashboardHelper } from '@/Pages/GenericWorklist/Stores/CurrentWorklistDashboardHelper';
import { ICustomWorklistHelper } from '@/Pages/GenericWorklist/Stores/CustomWorklistHelper';
import { makeAutoObservable } from 'mobx';
import { ZodSafeParseResult } from 'zod';

type ButtonState = 'disabled' | 'enabled' | 'hidden' | 'loading';

export interface IWorklistToDashboardDialogViewStore {
    readonly abbreviation: string | undefined;
    readonly addButtonState: ButtonState;
    readonly buttonColor: 'silent' | 'primary';
    readonly cancelButtonState: 'disabled' | 'enabled';
    readonly canOpenDialog: boolean;
    readonly currentWorklistTitle?: string;
    readonly deleteButtonState: ButtonState;
    readonly hasButtonOutline: boolean;
    readonly highThresholdString: string;
    readonly isDialogOpen: boolean;
    readonly mediumThresholdString: string;
    readonly renderedErrors: Record<WorklistToDashboardDialogErrorProperty, string[]>;
    readonly saveButtonState: ButtonState;
    commitFormInput: (key: WorklistToDashboardDialogProperty) => void;
    delete: () => Promise<void>;
    save: () => Promise<void>;
    setAbbreviation: (abbreviation?: string) => void;
    setHighThreshold: (highThreshold?: string) => void;
    setMediumThreshold: (mediumThreshold?: string) => void;
    toggleIsDialogOpen: (isOpen?: boolean) => void;
}

type WorklistToDashboardDialogProperty = keyof WorklistToDashboardDialogValidatedData;
// Kept in sync with the keys of WorklistToDashboardDialogValidatedData, plus a 'general' bucket for non-field errors.
export type WorklistToDashboardDialogErrorProperty = 'abbreviation' | 'highThreshold' | 'mediumThreshold' | 'general';
type DialogState = 'valid' | 'invalid' | 'savePending' | 'deletePending';

const DEFAULT_MEDIUM_THRESHOLD = 1;
const DEFAULT_HIGH_THRESHOLD = 5;

export class WorklistToDashboardDialogViewStore implements IWorklistToDashboardDialogViewStore {
    private formInputIsCommited: Record<WorklistToDashboardDialogProperty, boolean>;
    private pendingAction: 'save' | 'delete' | null = null;
    private userProvidedAbbreviation?: string;
    private userProvidedHighThreshold?: string;
    private userProvidedMediumThreshold?: string;

    isDialogOpen: boolean;

    constructor(
        private readonly currentWorklistInDashboardStore: ICurrentWorklistDashboardHelper,
        private readonly customWorklistsStore: ICustomWorklistHelper,
    ) {
        makeAutoObservable(this);

        this.isDialogOpen = false;
        this.formInputIsCommited = {
            abbreviation: false,
            highThreshold: false,
            mediumThreshold: false,
        };
    }

    get abbreviation(): string | undefined {
        const currentWorklistDashboardAbbreviation = this.currentWorklistInDashboardStore.currentWorklistDashboardTickerInfo?.Info.Abbreviation;

        return this.userProvidedAbbreviation
            ?? currentWorklistDashboardAbbreviation;
    }

    get addButtonState(): ButtonState {
        const isExistingDashboard = this.currentWorklistInDashboardStore.isWorklistAlreadyInDashboard;
        if (isExistingDashboard) {
            return 'hidden';
        }

        return this.submitButtonState;
    }

    get buttonColor(): 'silent' | 'primary' {
        return this.currentWorklistInDashboardStore.isWorklistAlreadyInDashboard
            ? 'primary'
            : 'silent';
    }

    get cancelButtonState(): 'disabled' | 'enabled' {
        switch (this.dialogState) {
            case 'savePending':
            case 'deletePending':
                return 'disabled';
            default:
                return 'enabled';
        }
    }

    get deleteButtonState(): ButtonState {
        const isExistingDashboard = this.currentWorklistInDashboardStore.isWorklistAlreadyInDashboard;
        if (!isExistingDashboard) {
            return 'hidden';
        }

        switch (this.dialogState) {
            case 'deletePending':
                return 'loading';
            case 'savePending':
                return 'disabled';
            default:
                return 'enabled';
        }
    }

    get dialogState(): DialogState {
        if (this.pendingAction === 'delete') {
            return 'deletePending';
        }
        if (this.pendingAction === 'save') {
            return 'savePending';
        }

        const anyErrors = Object.values(this.errors)
            .some((errorList) => errorList.length > 0);

        if (anyErrors) {
            return 'invalid';
        }

        return 'valid';
    }

    get canOpenDialog(): boolean {
        return !!this.customWorklistsStore.currentlySelectedCustomWorklist;
    }

    get currentWorklistTitle(): string | undefined {
        return this.customWorklistsStore.currentlySelectedCustomWorklist?.Name;
    }

    get hasButtonOutline(): boolean {
        return this.currentWorklistInDashboardStore.isWorklistAlreadyInDashboard;
    }

    get highThresholdString(): string {
        const currentWorklistDashboardHighThreshold = this.currentWorklistInDashboardStore.currentWorklistDashboardTickerInfo?.Info.HighThreshold;
        const fallbackIfNoUserProvided = currentWorklistDashboardHighThreshold ?? DEFAULT_HIGH_THRESHOLD;

        return (this.userProvidedHighThreshold
            ?? `${fallbackIfNoUserProvided}`);
    }

    get mediumThresholdString(): string {
        const currentWorklistDashboardMediumThreshold = this.currentWorklistInDashboardStore.currentWorklistDashboardTickerInfo?.Info.MediumThreshold;
        const fallbackIfNoUserProvided = currentWorklistDashboardMediumThreshold ?? DEFAULT_MEDIUM_THRESHOLD;

        return this.userProvidedMediumThreshold
            ?? `${fallbackIfNoUserProvided}`;
    }

    get errors(): Record<WorklistToDashboardDialogErrorProperty, string[]> {
        return {
            general: this.currentWorklistInDashboardStore.isNoSpaceLeftForCurrentNewDashboard ? ['Worklist.NoSpotsLeftInDashboard'] : [],
            abbreviation: getValidationErrorsForWorklistDialogInput('abbreviation', this.validationResult) ?? [],
            highThreshold: getValidationErrorsForWorklistDialogInput('highThreshold', this.validationResult) ?? [],
            mediumThreshold: getValidationErrorsForWorklistDialogInput('mediumThreshold', this.validationResult) ?? [],
        };
    }

    get renderedErrors(): Record<WorklistToDashboardDialogErrorProperty, string[]> {
        return {
            general: this.errors.general,
            abbreviation: this.formInputIsCommited.abbreviation ? this.errors.abbreviation : [],
            highThreshold: this.formInputIsCommited.highThreshold ? this.errors.highThreshold : [],
            mediumThreshold: this.formInputIsCommited.mediumThreshold ? this.errors.mediumThreshold : [],
        };
    }

    get saveButtonState(): ButtonState {
        const isExistingDashboard = this.currentWorklistInDashboardStore.isWorklistAlreadyInDashboard;
        if (!isExistingDashboard) {
            return 'hidden';
        }
        return this.submitButtonState;
    }

    get submitButtonState(): 'disabled' | 'enabled' | 'loading' {
        switch (this.dialogState) {
            case 'savePending':
                return 'loading';
            case 'valid':
                return 'enabled';
            default:
                return 'disabled';
        }
    }

    get validationResult(): ZodSafeParseResult<WorklistToDashboardDialogValidatedData> {
        const formData = {
            abbreviation: this.abbreviation ?? '',
            highThreshold: this.highThresholdString,
            mediumThreshold: this.mediumThresholdString,
        };

        const validationResult = validateWorklistToDashboardDialogInputData(formData);

        return validationResult;
    }

    commitFormInput = (key: WorklistToDashboardDialogProperty): void => {
        this.formInputIsCommited[key] = true;
    };

    delete = async (): Promise<void> => {
        this.pendingAction = 'delete';
        await this.currentWorklistInDashboardStore.deleteWorklistDashboard();
        this.pendingAction = null;
        this.toggleIsDialogOpen(false);
    };

    resetUserProvidedValues = (): void => {
        this.setAbbreviation();
        this.setHighThreshold();
        this.setMediumThreshold();
        this.formInputIsCommited.abbreviation = false;
        this.formInputIsCommited.highThreshold = false;
        this.formInputIsCommited.mediumThreshold = false;
    };

    save = async (): Promise<void> => {
        const data = this.validationResult.data;
        if (!data) {
            throw new Error(`Data is not valid to save`);
        }

        const { abbreviation, highThreshold, mediumThreshold } = data;
        this.pendingAction = 'save';
        await this.currentWorklistInDashboardStore.saveWorklistDashboard(abbreviation, highThreshold, mediumThreshold);
        this.pendingAction = null;
        this.toggleIsDialogOpen(false);
    };

    setAbbreviation = (value?: string): void => {
        this.userProvidedAbbreviation = value;
        this.formInputIsCommited.abbreviation = false;
    };

    setHighThreshold = (value?: string): void => {
        this.userProvidedHighThreshold = value;
        this.formInputIsCommited.highThreshold = false;
    };

    setMediumThreshold = (value?: string): void => {
        this.userProvidedMediumThreshold = value;
        this.formInputIsCommited.mediumThreshold = false;
    };

    toggleIsDialogOpen = (isOpen?: boolean): void => {
        const targetState = isOpen ?? !this.isDialogOpen;
        if (targetState && !this.canOpenDialog) {
            throw new Error(`Can't open the dialog`);
        }

        if (!targetState) {
            this.resetUserProvidedValues();
        }

        this.isDialogOpen = targetState;
    };
}
